"use client"

import { AnimatePresence, motion, useDragControls } from "framer-motion"
import { X } from "lucide-react"
import { useEffect } from "react"
import { createPortal } from "react-dom"

/**
 * Bottom sheet with a blurred scrim backdrop.
 * Springs up from the bottom. Closes via scrim click, X button, Escape,
 * or swipe-down that starts inside the drag zone at the top of the sheet.
 * Threshold: drag past ~40% of the sheet height OR flick down past ~600 px/s.
 */
export default function BottomSheet({ open, onClose, title, children }) {
  const dragControls = useDragControls()

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === "Escape") onClose() }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => { document.body.style.overflow = prev }
  }, [open])

  if (typeof document === "undefined") return null

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[1100]">
          {/* Backdrop scrim */}
          <motion.div
            className="absolute inset-0 bg-slate-950/45 backdrop-blur-[3px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
            onClick={onClose}
          />
          {/* Sheet — drag is only initiated from the top handle zone below.
              Elastic only at the bottom edge so an upward drag doesn't lift the sheet. */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={title || "Dialog"}
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-[28px] shadow-[0_-20px_60px_-20px_rgba(0,20,60,0.35)] max-h-[85vh] flex flex-col mx-auto max-w-3xl overflow-hidden"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 340, mass: 0.85 }}
            drag="y"
            dragListener={false}
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            onDragEnd={(_, info) => {
              // Dismiss on a decisive downward gesture: past ~110px, or a flick.
              if (info.offset.y > 110 || info.velocity.y > 600) onClose()
            }}
          >
            {/* Drag handle zone — pt-3 + pill + pb-2 gives a ~34px hit target
                for the ~6px pill, so users don't need to touch it exactly. */}
            <div
              onPointerDown={(e) => dragControls.start(e)}
              style={{ touchAction: "none" }}
              className="flex flex-col items-center pt-3 pb-2 cursor-grab active:cursor-grabbing select-none"
            >
              <div className="w-10 h-1.5 bg-slate-300 rounded-full" />
            </div>
            {/* Header — also part of the drag zone (extra forgiveness). */}
            {title && (
              <div
                onPointerDown={(e) => {
                  // Don't initiate drag when tapping the close button
                  if (e.target.closest("button")) return
                  dragControls.start(e)
                }}
                style={{ touchAction: "none" }}
                className="flex items-center justify-between px-6 pt-1 pb-1 cursor-grab active:cursor-grabbing"
              >
                <h3 className="text-xl font-semibold text-[#004aad] select-none">{title}</h3>
                <button
                  onClick={onClose}
                  className="press p-2 -mr-2 hover:bg-slate-100 rounded-full text-slate-500 hover:text-slate-700 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
            {/* Content — scrolls normally; drag isn't initiated here. */}
            <div className="px-6 pb-8 pt-3 overflow-y-auto flex-1">
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  )
}
